import { useState } from 'react';
import { Check, Circle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useUpdateProjectScopingStage,
  PROJECT_SCOPING_STAGES,
  type ScopingStage,
  type ProjectScopingStageInfo,
} from '@/hooks/useProjects';

interface ProjectScopingTimelineProps {
  projectId: string;
  currentStage: ScopingStage | null;
  stages: ProjectScopingStageInfo[];
  onStageUpdate?: () => void;
}

export function ProjectScopingTimeline({
  projectId,
  currentStage,
  stages,
  onStageUpdate,
}: ProjectScopingTimelineProps) {
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const updateStage = useUpdateProjectScopingStage();

  const getStageData = (stageId: ScopingStage): ProjectScopingStageInfo | undefined => {
    return stages.find((s) => s.stageId === stageId);
  };

  const handleStatusChange = async (
    stageId: ScopingStage,
    newStatus: 'not_started' | 'in_progress' | 'completed'
  ) => {
    try {
      await updateStage.mutateAsync({
        projectId,
        stageId,
        status: newStatus,
        completedDate: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : null,
      });
      onStageUpdate?.();
    } catch (error) {
      console.error('Failed to update stage:', error);
    }
  };

  const handleFieldUpdate = async (
    stageId: ScopingStage,
    data: Partial<Omit<ProjectScopingStageInfo, 'id' | 'projectId' | 'stageId'>>
  ) => {
    const stageData = getStageData(stageId);
    if (!stageData) return;

    try {
      await updateStage.mutateAsync({
        projectId,
        stageId,
        status: stageData.status,
        ...data,
      });
      onStageUpdate?.();
    } catch (error) {
      console.error('Failed to update stage:', error);
    }
  };

  return (
    <div className="card-form relative overflow-hidden">
      <h3 className="font-heading text-lg font-semibold text-xtravirt-dark mb-4">
        Scoping Progress
      </h3>

      <div className="space-y-1 relative">
        {PROJECT_SCOPING_STAGES.map((stage, index) => {
          const stageData = getStageData(stage.id);
          const status = stageData?.status || 'not_started';
          const isExpanded = expandedStage === stage.id;
          const isCurrent = stage.id === currentStage;
          const isCompleted = status === 'completed';
          const isInProgress = status === 'in_progress';
          const isLast = index === PROJECT_SCOPING_STAGES.length - 1;

          return (
            <div key={stage.id} className="relative">
              {/* Stage Row */}
              <div
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                  isCurrent && 'bg-xtravirt-cyan/5 border border-xtravirt-cyan/20',
                  isExpanded && 'bg-gray-50',
                  !isCurrent && !isExpanded && 'hover:bg-gray-50'
                )}
                onClick={() => setExpandedStage(isExpanded ? null : stage.id)}
              >
                {/* Status Icon */}
                <div className="flex-shrink-0">
                  {isCompleted ? (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white">
                      <Check className="h-4 w-4" />
                    </div>
                  ) : isInProgress ? (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-xtravirt-cyan text-white">
                      <Clock className="h-4 w-4" />
                    </div>
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-300">
                      <Circle className="h-3 w-3 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Connector Line */}
                {!isLast && (
                  <div className="absolute left-[1.5rem] top-[2.25rem] bottom-0 w-0.5 bg-gray-200" />
                )}

                {/* Stage Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'font-medium',
                        isCompleted && 'text-green-700',
                        isInProgress && 'text-xtravirt-cyan',
                        !isCompleted && !isInProgress && 'text-gray-500'
                      )}
                    >
                      {stage.name}
                    </span>
                    {stageData?.confidence && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                        {stageData.confidence}% confident
                      </span>
                    )}
                  </div>
                  {stageData?.targetDate && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Target: {new Date(stageData.targetDate).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {/* Expand Toggle */}
                <div className="flex-shrink-0">
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="ml-6 md:ml-9 mt-2 p-3 md:p-4 bg-gray-50 rounded-lg space-y-4">
                  {/* Status Buttons */}
                  <div>
                    <label className="label text-xs mb-2">Status</label>
                    <div className="flex gap-2">
                      {(['not_started', 'in_progress', 'completed'] as const).map((s) => (
                        <button
                          key={s}
                          onClick={() => handleStatusChange(stage.id, s)}
                          disabled={updateStage.isPending}
                          className={cn(
                            'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                            status === s
                              ? s === 'completed'
                                ? 'bg-green-500 text-white'
                                : s === 'in_progress'
                                ? 'bg-xtravirt-cyan text-white'
                                : 'bg-gray-500 text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                          )}
                        >
                          {s === 'not_started'
                            ? 'Not Started'
                            : s === 'in_progress'
                            ? 'In Progress'
                            : 'Completed'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label className="label text-xs">Target Date</label>
                      <input
                        type="date"
                        value={stageData?.targetDate || ''}
                        onChange={(e) =>
                          handleFieldUpdate(stage.id, { targetDate: e.target.value || null })
                        }
                        className="input mt-1 w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="label text-xs">Completed Date</label>
                      <input
                        type="date"
                        value={stageData?.completedDate || ''}
                        onChange={(e) =>
                          handleFieldUpdate(stage.id, { completedDate: e.target.value || null })
                        }
                        className="input mt-1 w-full text-sm"
                      />
                    </div>
                  </div>

                  {/* Confidence */}
                  <div>
                    <label className="label text-xs">Confidence (%)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={stageData?.confidence || ''}
                      onChange={(e) =>
                        handleFieldUpdate(stage.id, {
                          confidence: e.target.value ? parseInt(e.target.value, 10) : null,
                        })
                      }
                      placeholder="0-100"
                      className="input mt-1 w-24 text-sm"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="label text-xs">Notes</label>
                    <textarea
                      value={stageData?.notes || ''}
                      onChange={(e) =>
                        handleFieldUpdate(stage.id, { notes: e.target.value || null })
                      }
                      rows={2}
                      placeholder="Stage notes..."
                      className="input mt-1 w-full text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
